import { type Component, PureComponent, type ReactElement } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { connect } from "react-redux";

import { CalloutIntent, ColoredLabel, TabItem } from "widgets";

import { type ITableSpecWithContext } from "../../../content-browser/ContentBrowserTableView.js";
import ColumnSelectionButton from "../../../core/ColumnSelectionButton.js";
import { Checkbox } from "../../../core/forms/Checkbox.js";
import {
  GenericSortSelectionForm,
  type IGenericSortSelectionFormProps,
  type IGenericSortSelectionFormState,
} from "../../../core/forms/GenericSortSelectionForm.js";
import DataTableContainer from "../../../core/grid/DataTableContainer.js";
import PageHeader from "../../../core/navigation-panel/PageHeader.js";
import { TabDialog } from "../../../core/overlays/Dialogs.js";
import { VariantSymbol } from "../VariantSymbol.js";
import PhenotypeSelector from "./PhenotypeSelector.js";
import MutantSortSelection from "./VcfMutantGenomesSelectionForm.js";
import { getVariantEffectColor } from "@/app-utils/color.ts";
import { type IRsqlComparisonExpr, SEARCH_OPERATORS, parse, toComparisonArray } from "@/app-utils/rsql.ts";
import {
  type ISelectionModel,
  type TGridSelModelIdentifier,
  createSelectionModel,
} from "@/app-utils/selection-model.ts";
import { generateVariantStyles } from "@/app-utils/track-shapes/shape-variant.tsx";
import { organismToMutantGenome } from "@/app-utils/variant.ts";
import {
  type GenomicFeature,
  type InputTableQuerySpec,
  type KeyLabel,
  type Organism,
  type TrackCustomization,
  type TrackPhenotypeConfiguration,
  type VariantAnalysisGroup,
} from "@/graphql.ts";
import {
  type IConfiguredPhenotype,
  type IGenomeBrowserState,
  type IMutantGenome,
  type ISortableTrack,
  createVariantFilterPayload,
  fetchApplicableOrgPropertyTypes,
  getMutantGenomesByAnalysisGroup,
  isSamePhenotypeType,
  setGenomeBrowserPhenotypeConfiguration,
  setSelectedMutantGenomeList,
} from "@/redux/modules/genome-browser.ts";
import {
  type IFilterPayload,
  type ITableColumn,
  type ITableData,
  createTableSpecForAnalysisGroupParent,
  createTableSpecForOrganismParent,
  createUniqueKeyFromTableSpec,
  filterByTableColumn,
  getTableDataSlice,
  toggleSelection,
} from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { ORGANISM_TABLE_COLUMNS, TRACK_TYPE, UNKNOWN_GENOME_ID, VARIANT_TABLE_COLUMNS } from "@/settings/settings.ts";

export interface IVariantsTrackCustomizationDialogOwnProps {
  type: string;

  onApply: () => void;
  onCancel: () => void;
}

export interface IVariantFilterableProperty extends KeyLabel {
  filtered: boolean;
}

interface IVariantsTrackCustomizationDialogAppStateProps {
  selectedMutantGenomes: IMutantGenome[];
  variantsTableColumns: ITableColumn[];
  variantsTableSpec: InputTableQuerySpec;
  variantsFilterExpression: string;
  maxSelectableMutantGenomesAsVariantRestriction: number;
  refGenomeId: number;
  isReferencePerspective: boolean;
  isLoadingApplicableOrgPropertyTypes: boolean;
  applicableOrgPropertyTypes: GenomicFeature[];
  trackCustomization: TrackCustomization;
  genome: Organism;
  isDerivedVariantsMutantView: boolean;
}

interface IVariantsTrackCustomizationDialogDispatchProps {
  setSelectedMutantGenomeList: (mutantGenomes: IMutantGenome[]) => void;
  setGenomeBrowserPhenotypeConfiguration: (trackType: string, phenotypesConfig: TrackPhenotypeConfiguration[]) => void;
  setVariantsFilters: (filterPayload: IFilterPayload[]) => void;
  loadApplicableOrgPropertyTypes: (orgIds: number[]) => Promise<GenomicFeature[]>;
  clearSelection: (tableQuerySpec: InputTableQuerySpec) => void;
  getMutantGenomesByAnalysisGroup: (
    analysisGroup: VariantAnalysisGroup,
    selectedGenomeIds?: number[],
  ) => Promise<Organism[]>;
  filterByTableColumn: (filter: IFilterPayload) => Promise<any>;
}

export interface IVariantsTrackCustomizationDialogProps
  extends IVariantsTrackCustomizationDialogOwnProps,
    IVariantsTrackCustomizationDialogAppStateProps,
    IVariantsTrackCustomizationDialogDispatchProps {}

function mapStateToProps(
  state: IAppState,
  ownProps: IVariantsTrackCustomizationDialogOwnProps,
): IVariantsTrackCustomizationDialogAppStateProps {
  const genomeBrowser: IGenomeBrowserState = state.genomeBrowser;
  const genome: Organism = genomeBrowser.genome;
  const refGenomeId: number = state.auth.session?.params.referenceGenome;
  const tableSpec: InputTableQuerySpec = !genomeBrowser.isLoading && createTableSpecForOrganismParent(ownProps.type);
  const variantsDataSlice: ITableData = tableSpec && getTableDataSlice(state.tableData, tableSpec);

  const track: ISortableTrack = genomeBrowser.tracks.find((track) => track.type === ownProps.type);

  return {
    selectedMutantGenomes: genomeBrowser.selectedMutantGenomes,
    variantsTableColumns: variantsDataSlice?.columns,
    variantsTableSpec: variantsDataSlice?.tableQuerySpec, // this property is not created on the fly and therefore is stable
    variantsFilterExpression: variantsDataSlice?.filterExpression,
    maxSelectableMutantGenomesAsVariantRestriction: Number.parseInt(
      state.config.configuration?.application?.maxVisibleVariantGenomes,
      10,
    ),
    refGenomeId: refGenomeId,
    isReferencePerspective: genome && (ownProps.type === TRACK_TYPE.VCF_VARIANT || genome.id === refGenomeId),
    isLoadingApplicableOrgPropertyTypes: genomeBrowser.isLoadingApplicableOrgPropertyTypes,
    applicableOrgPropertyTypes: genomeBrowser.applicableOrgPropertyTypes,
    trackCustomization: track?.trackCustomization,
    genome: genomeBrowser.genome,
    isDerivedVariantsMutantView:
      ownProps.type === TRACK_TYPE.VARIANT &&
      genomeBrowser.contig.organism.id !== UNKNOWN_GENOME_ID &&
      genomeBrowser.contig.organism.id !== refGenomeId,
  };
}

function mapDispatchToProps(dispatch): IVariantsTrackCustomizationDialogDispatchProps {
  return {
    setSelectedMutantGenomeList: (mutantGenomes: IMutantGenome[]) =>
      dispatch(setSelectedMutantGenomeList(mutantGenomes)),
    setVariantsFilters: (filterPayload: IFilterPayload[]) => dispatch(filterByTableColumn(filterPayload)),
    setGenomeBrowserPhenotypeConfiguration: (trackType: string, phenotypeConfig: TrackPhenotypeConfiguration[]) =>
      dispatch(setGenomeBrowserPhenotypeConfiguration(trackType, phenotypeConfig)),
    loadApplicableOrgPropertyTypes: (orgIds: number[]) => dispatch(fetchApplicableOrgPropertyTypes(orgIds)),
    clearSelection: (tableQuerySpec: InputTableQuerySpec) => dispatch(toggleSelection(false, tableQuerySpec)),
    getMutantGenomesByAnalysisGroup: (analysisGroup: VariantAnalysisGroup, selectedGenomeIds: number[] = undefined) =>
      dispatch(getMutantGenomesByAnalysisGroup(analysisGroup, selectedGenomeIds)),
    filterByTableColumn: (filter: IFilterPayload) => dispatch(filterByTableColumn(filter)),
  };
}

export interface IVariantsTrackCustomizationDialogState {
  mutantGenomes?: IMutantGenome[];
  selectedMutantGenomes?: IMutantGenome[];
  variantEffects?: IVariantFilterableProperty[];
  variantTypes?: IVariantFilterableProperty[];
  selectedPhenotypes?: IConfiguredPhenotype[];
  warning?: string | null;
}

class VariantsTrackCustomizationDialog extends PureComponent<
  IVariantsTrackCustomizationDialogProps,
  IVariantsTrackCustomizationDialogState
> {
  constructor(props: IVariantsTrackCustomizationDialogProps) {
    super(props);

    this.state = {
      mutantGenomes: [], // need full list in the case of derived variants (not for VCF variants since we use a dynamic server side table for selection of VCF mutant genomes)
      selectedMutantGenomes: [],
      variantEffects: [],
      variantTypes: [],
      selectedPhenotypes: [],
      warning: null,
    };

    this.onVariantTypeCheckboxClick = this.onVariantTypeCheckboxClick.bind(this);
    this.onVariantEffectCheckboxClick = this.onVariantEffectCheckboxClick.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleVariantEffectSelection = this.handleVariantEffectSelection.bind(this);
    this.handleVariantTypeSelection = this.handleVariantTypeSelection.bind(this);
    this.renderVariantTypeItem = this.renderVariantTypeItem.bind(this);
    this.renderVariantEffectItem = this.renderVariantEffectItem.bind(this);
  }

  componentDidMount() {
    this.loadMutantGenomes();
    this.initState();
  }

  onVcfMutantsSelectionModelChanged = (selectionModel: ISelectionModel) => {
    const selectedGenomeIds: TGridSelModelIdentifier[] = selectionModel.selectedIds.slice(
      0,
      this.props.maxSelectableMutantGenomesAsVariantRestriction,
    );
    if (selectedGenomeIds.length == 0) {
      this.setState({ selectedMutantGenomes: [] });
      return;
    }

    const analysisGroup: VariantAnalysisGroup = this.props.genome.variantAnalysisGroups?.[0];
    void this.props.getMutantGenomesByAnalysisGroup(analysisGroup, selectedGenomeIds as number[]).then((organisms) => {
      const mutantGenomes = organisms.filter((o) => !o.isReference).map(organismToMutantGenome);
      this.setState({
        selectedMutantGenomes: mutantGenomes,
        warning:
          selectionModel.selectedIds.length > this.props.maxSelectableMutantGenomesAsVariantRestriction
            ? `You cannot select more than ${this.props.maxSelectableMutantGenomesAsVariantRestriction} items`
            : null,
      });
    });
  };

  loadMutantGenomes = () => {
    // there is always only one single analysis group
    const analysisGroup: VariantAnalysisGroup = this.props.genome.variantAnalysisGroups?.[0];
    if (analysisGroup) {
      void this.props.getMutantGenomesByAnalysisGroup(analysisGroup).then((organisms) => {
        const mutantGenomes = organisms.filter((o) => o.id !== this.props.refGenomeId);
        this.setState({ mutantGenomes: mutantGenomes });
      });
    }
    // the VCF case does not need to download upfront the list of mutant genomes
  };

  componentDidUpdate(
    prevProps: Readonly<IVariantsTrackCustomizationDialogProps>,
    prevState: Readonly<IVariantsTrackCustomizationDialogState>,
  ) {
    if (this.props.genome !== prevProps.genome) {
      this.loadMutantGenomes();
    }
    if (this.state.selectedMutantGenomes !== prevState.selectedMutantGenomes) {
      this.loadPhenotypesForSelectedGenomes();
    }

    if (this.props.applicableOrgPropertyTypes !== prevProps.applicableOrgPropertyTypes) {
      this.cleanupSelectedPhenotypes();
    }
  }

  loadPhenotypesForSelectedGenomes = () => {
    void this.props
      .loadApplicableOrgPropertyTypes(this.state.selectedMutantGenomes.map((g) => g.id))
      .then((phenotypes: GenomicFeature[]) => {
        if (this.props.trackCustomization?.configuredPhenotypes) {
          const selectedPhenotypes: IConfiguredPhenotype[] = this.props.trackCustomization?.configuredPhenotypes
            .filter((configuredPhenotype: TrackPhenotypeConfiguration) =>
              phenotypes.find((p) => isSamePhenotypeType(p, configuredPhenotype)),
            )
            .map((configuredPhenotype: TrackPhenotypeConfiguration) => {
              const phenotypeType: GenomicFeature = phenotypes.find((p) => isSamePhenotypeType(p, configuredPhenotype));
              return {
                ...phenotypeType,
                ...configuredPhenotype,
              }; // as IConfiguredPhenotype;
            });
          this.setState({
            selectedPhenotypes: selectedPhenotypes,
          });
        }
      });
  };

  initState = () => {
    const props: IVariantsTrackCustomizationDialogProps = this.props;
    if (props.variantsTableColumns && props.variantsTableColumns.length > 0) {
      const filterConstraints: IRsqlComparisonExpr[] =
        props.variantsFilterExpression &&
        (toComparisonArray(parse(props.variantsFilterExpression)) as IRsqlComparisonExpr[]);

      this.setState({
        variantEffects: this.initVariantFilterState(
          props.variantsTableColumns,
          filterConstraints,
          VARIANT_TABLE_COLUMNS.EFFECT,
        ),
        variantTypes: this.initVariantFilterState(
          props.variantsTableColumns,
          filterConstraints,
          VARIANT_TABLE_COLUMNS.TYPE,
        ),
      });
      if (props.selectedMutantGenomes) {
        this.setState({
          selectedMutantGenomes: props.selectedMutantGenomes,
        });
      }
    }
  };

  cleanupSelectedPhenotypes = () => {
    // remove phenotypes from selection which are not included in the list of applicable phenotypes anymore
    this.setState({
      selectedPhenotypes: this.state.selectedPhenotypes.filter((p) =>
        this.props.applicableOrgPropertyTypes.find((a) => isSamePhenotypeType(a, p)),
      ),
    });
  };

  initVariantFilterState(
    variantsTableColumns: ITableColumn[],
    filterConstraints: IRsqlComparisonExpr[],
    field: string,
  ): IVariantFilterableProperty[] | null | undefined {
    const filterTableColumn: ITableColumn | undefined = Array.isArray(variantsTableColumns)
      ? variantsTableColumns.find((col: ITableColumn) => col.field === field)
      : undefined;

    const rsqlFilter = Array.isArray(filterConstraints)
      ? filterConstraints.find((filter: IRsqlComparisonExpr) => filter.key === field)
      : undefined;
    // we can safely assume that the value of the filter is actually an array of strings (IN or NOT IN operators)
    const variantFilterValues: KeyLabel[] | null =
      (filterTableColumn?.searchAttributes?.values as KeyLabel[] | null) ?? null;

    // we loop through the different available values and create a IVariantFilterableProperty object representing the value with its checked state
    return variantFilterValues?.map((filter) => {
      // we can safely assume that the value of the filter is actually an array of strings (IN or NOT IN operators)
      const filtered: boolean | undefined =
        rsqlFilter && !!(rsqlFilter.value as string[]).find((value: string) => value === filter.key);
      return {
        filtered:
          rsqlFilter && typeof filtered === "boolean"
            ? rsqlFilter.operator === SEARCH_OPERATORS.IN
              ? filtered
              : !filtered
            : true,
        ...filter,
      };
    });
  }

  setSelectedPhenotypes = (selectedPhenotypes: IConfiguredPhenotype[]) => {
    this.setState({ selectedPhenotypes: selectedPhenotypes });
  };

  handleVariantEffectSelection(filteredVariantEffects: IVariantFilterableProperty[], select: boolean) {
    const variantEffects: IVariantFilterableProperty[] = this.state.variantEffects.map(
      (fp: IVariantFilterableProperty) => {
        const filtered = !!filteredVariantEffects.find(
          (filteredEffect: IVariantFilterableProperty) => filteredEffect.key === fp.key,
        );
        return { ...fp, filtered: filtered ? select : !select };
      },
    );
    this.setState({ variantEffects: variantEffects });
  }

  handleVariantTypeSelection(filteredVariantTypes: IVariantFilterableProperty[], select: boolean) {
    const variantTypes: IVariantFilterableProperty[] = this.state.variantTypes.map((fp: IVariantFilterableProperty) => {
      const filtered = !!filteredVariantTypes.find(
        (filteredType: IVariantFilterableProperty) => filteredType.key === fp.key,
      );
      return { ...fp, filtered: filtered ? select : !select };
    });
    this.setState({ variantTypes: variantTypes });
  }

  // not draggable, so not using DragDropItem !
  renderFilterableVariantPropertyItem(
    index: number,
    item: IVariantFilterableProperty,
    label: ReactElement,
    onCheckboxClick: (checked: boolean, item: KeyLabel) => void,
  ): ReactElement {
    return (
      <div
        key={`variant-prop-item-${item.key}`}
        id={`variant-prop-item-${index}`}
        className="form-group"
        style={{ position: "relative" }}
      >
        <div className="checkbox check-default" style={{ marginTop: 0, marginBottom: 0, marginLeft: 15 }}>
          <Checkbox
            id={"variant-prop_" + item.key}
            label={label}
            disabled={false}
            checked={item.filtered}
            onChange={(event) => onCheckboxClick(event.target.checked, item)}
          />
        </div>
      </div>
    );
  }

  handleCancel() {
    this.initState(); // probably not needed seems we unmount we closing
    this.props.onCancel();
  }

  handleApply = () => {
    const filters: IFilterPayload[] = [];

    // process filter of variant types
    const typeTableColumn: ITableColumn = this.props.variantsTableColumns?.find(
      (col: ITableColumn) => col.field === VARIANT_TABLE_COLUMNS.TYPE,
    );

    const typesFilterPayload: IFilterPayload = createVariantFilterPayload(
      this.state.variantTypes,
      typeTableColumn,
      this.props.variantsTableSpec,
      VARIANT_TABLE_COLUMNS.TYPE,
    );

    filters.push(typesFilterPayload);

    if (this.props.type === TRACK_TYPE.VARIANT) {
      const effectTableColumn: ITableColumn = this.props.variantsTableColumns?.find(
        (col: ITableColumn) => col.field === VARIANT_TABLE_COLUMNS.EFFECT,
      );

      const effectsFilterPayload: IFilterPayload = createVariantFilterPayload(
        this.state.variantEffects,
        effectTableColumn,
        this.props.variantsTableSpec,
        VARIANT_TABLE_COLUMNS.EFFECT,
      );

      filters.push(effectsFilterPayload);
    }

    // keep the order consistent for derived mutant genomes (in case of VCF mutants the order is already naturally coming from the server)
    let orderedSelectedMutantGenomes = this.state.selectedMutantGenomes;
    if (this.props.type === TRACK_TYPE.VARIANT) {
      orderedSelectedMutantGenomes = this.state.mutantGenomes.filter((genome) =>
        this.state.selectedMutantGenomes.find((selectedMutant) => selectedMutant.id === genome.id),
      );
    }
    this.props.setSelectedMutantGenomeList(orderedSelectedMutantGenomes);

    // set filters, even if all options are selected we want to overwrite any existing filter
    this.props.setVariantsFilters(filters);

    // trigger modification of selected phenotypes
    const phenotypeConfiguration: TrackPhenotypeConfiguration[] = this.state.selectedPhenotypes.map(
      (selectedPhenotype: IConfiguredPhenotype) => {
        const phenotypeConfiguration: TrackPhenotypeConfiguration = {
          id: selectedPhenotype.id,
          idType: selectedPhenotype.idType,
          minValue: selectedPhenotype.minValue,
          maxValue: selectedPhenotype.maxValue,
          logScale: selectedPhenotype.logScale,
          colorScheme: selectedPhenotype.colorScheme,
          configuredTerms: selectedPhenotype.configuredTerms,
        };
        return phenotypeConfiguration;
      },
    );
    this.props.setGenomeBrowserPhenotypeConfiguration(this.props.type, phenotypeConfiguration);

    this.props.onApply();
  };

  onVariantEffectCheckboxClick(checked: boolean, item: KeyLabel) {
    this.setState({
      variantEffects: this.state.variantEffects.map((variantEffect: IVariantFilterableProperty) =>
        variantEffect.key === item.key ? { ...variantEffect, filtered: checked } : variantEffect,
      ),
    });
  }

  onVariantTypeCheckboxClick(checked: boolean, item: KeyLabel) {
    this.setState({
      variantTypes: this.state.variantTypes.map((variantType: IVariantFilterableProperty) =>
        variantType.key === item.key ? { ...variantType, filtered: checked } : variantType,
      ),
    });
  }

  renderVariantTypeItem(index: number, item: IVariantFilterableProperty) {
    const symbolStyle: any = generateVariantStyles(item.label).symbolElementStyle;
    const label: ReactElement = (
      <span>
        <VariantSymbol styleContent={symbolStyle} type={item.label} width={12} />
        &nbsp;{item.label}
      </span>
    );
    return this.renderFilterableVariantPropertyItem(index, item, label, this.onVariantTypeCheckboxClick);
  }

  renderVariantEffectItem(index: number, item: IVariantFilterableProperty) {
    const label: ReactElement = <ColoredLabel color={getVariantEffectColor(item.label)}>{item.label}</ColoredLabel>;
    return this.renderFilterableVariantPropertyItem(index, item, label, this.onVariantEffectCheckboxClick);
  }

  render(): ReactElement {
    // we create dynamically a generic component
    type KeyLabelReactCtor<P, S> = new (props: IGenericSortSelectionFormProps<KeyLabel>) => Component<P, S>;

    const VariantEffectSelectionForm: KeyLabelReactCtor<
      IGenericSortSelectionFormProps<KeyLabel>,
      IGenericSortSelectionFormState<KeyLabel>
    > = GenericSortSelectionForm;
    const VariantTypeSelectionForm: KeyLabelReactCtor<
      IGenericSortSelectionFormProps<KeyLabel>,
      IGenericSortSelectionFormState<KeyLabel>
    > = GenericSortSelectionForm;

    const onFilterVariantProps = (filterTerm: string, item: KeyLabel): boolean =>
      item.label.toLowerCase().includes(filterTerm?.toLowerCase());

    const analysisGroup: VariantAnalysisGroup = this.props.genome.variantAnalysisGroups?.[0];
    const vcfMutantGenomesTableSpec: ITableSpecWithContext = analysisGroup && {
      ...createTableSpecForAnalysisGroupParent(analysisGroup),
      context: "track-custom",
    };

    return (
      <TabDialog
        headerProps={{
          title: this.props.type === TRACK_TYPE.VCF_VARIANT ? "Variants Track" : "Derived Variants Track",
        }}
        keepAlive={true}
        mainActionButtonProps={{
          children: "Apply",
          onPress: this.handleApply,
          isDisabled: !!this.state.warning,
        }}
        footerMessage={{ message: this.state.warning, intent: CalloutIntent.Warning }}
        closeFn={this.handleCancel}
        fullScreen={this.props.type === TRACK_TYPE.VCF_VARIANT}
      >
        {!this.props.isDerivedVariantsMutantView ? (
          <TabItem key={"profile"} title={"Genomes"}>
            {this.props.type === TRACK_TYPE.VCF_VARIANT ? (
              <>
                <PageHeader
                  newStyle={true}
                  actionWidgets={[
                    <ColumnSelectionButton
                      portalContext={createUniqueKeyFromTableSpec(vcfMutantGenomesTableSpec)}
                      key={"variants-track-customization-dialog-action-1"}
                    />,
                  ]}
                />
                <div
                  className={"with-filter-on-top"}
                  style={{ backgroundColor: "white", height: "calc(100vh - 350px)" }}
                >
                  <DataTableContainer
                    key={createUniqueKeyFromTableSpec(vcfMutantGenomesTableSpec)}
                    tableSpec={vcfMutantGenomesTableSpec}
                    tableInitializationOptions={{
                      initialSelectionModel: createSelectionModel(this.state.selectedMutantGenomes.map((m) => m.id)),
                      sortExpression: ORGANISM_TABLE_COLUMNS.NAME,
                      //filterExpression: "isReference=='false'",
                    }}
                    onSelectionModelChanged={this.onVcfMutantsSelectionModelChanged}
                  />
                </div>
              </>
            ) : (
              <DndProvider backend={HTML5Backend}>
                <MutantSortSelection
                  mutantGenomes={this.state.mutantGenomes}
                  selectedMutantGenomes={this.state.selectedMutantGenomes}
                  setMutantGenomes={(mutantGenomes) => this.setState({ mutantGenomes: mutantGenomes })}
                  setSelectedMutantGenomes={(selectedMutantGenomes) => this.setState({ selectedMutantGenomes })}
                />
              </DndProvider>
            )}
          </TabItem>
        ) : null}

        <TabItem key={"types"} title={"Types"}>
          <DndProvider backend={HTML5Backend}>
            <VariantTypeSelectionForm
              tooltip="Modify the variant type selection"
              items={this.state.variantTypes || []}
              renderItem={this.renderVariantTypeItem}
              onFilter={onFilterVariantProps}
              handleSelection={this.handleVariantTypeSelection}
            />
          </DndProvider>
        </TabItem>

        {this.props.type !== TRACK_TYPE.VCF_VARIANT && (
          <TabItem key={"effect"} title={"Effect"}>
            <DndProvider backend={HTML5Backend}>
              <VariantEffectSelectionForm
                tooltip="Modify the variant effect selection"
                items={this.state.variantEffects || []}
                renderItem={this.renderVariantEffectItem}
                onFilter={onFilterVariantProps}
                handleSelection={this.handleVariantEffectSelection}
              />
            </DndProvider>
          </TabItem>
        )}
        {this.props.isReferencePerspective && (
          <TabItem key={"phenotypes"} title={"Data"}>
            <DndProvider backend={HTML5Backend}>
              <div>
                Select the phenotypes to display in the variants track. If genomes are associated with sample records,
                you may also select sample attributes.
              </div>
              <PhenotypeSelector
                availablePhenotypes={this.props.applicableOrgPropertyTypes}
                selectedPhenotypes={this.state.selectedPhenotypes}
                phenotypesUpdated={this.setSelectedPhenotypes}
                dropdownLabel={"Choose phenotypes or sample attributes"}
                isLoadingPhenotypes={this.props.isLoadingApplicableOrgPropertyTypes}
              />
            </DndProvider>
          </TabItem>
        )}
      </TabDialog>
    );
  }
}

export default connect<
  IVariantsTrackCustomizationDialogAppStateProps,
  IVariantsTrackCustomizationDialogDispatchProps,
  IVariantsTrackCustomizationDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(VariantsTrackCustomizationDialog);
